<template>
    <SubLayout
        :pageTitle="title"
        :pageBreadcrumbs="breadcrumbs"
        :hasBack="false"
    >
      <template #subHeaderActions>
        <div class="d-flex align-items-center justify-content-end">
          <DateRangePicker id="projects-daterangepicker" :start="query.start_date" :end="query.end_date" @update:date="handleRangeDate2" with-all-time />
          <ButtonDropdownFilter
              :btn-title="$t('label.allContacts')"
              :options="[...contacts]"
              value-field="id"
              name-field="fullname"
              v-model="query.contact_id"
          />
        </div>
      </template>
      <template #layoutContent>
        <Loader>
          <div class="card shadow-sm mb-5 p-5 " v-if="offices?.length > 1">
            <div class="d-flex justify-content-start align-items-center mt-2">
              <h3 class="mt-1"> {{ currentCompany?.name }} </h3>
              <span class="ms-5"> {{ $t('projects.selectCompanyDesc') }} </span>
            </div>
            <div class="row mt-5">
              <OfficeCountCard class="mb-4"  v-for="office in offices" :withCount="false" :key="office.id" :office="office" v-model="selectedOffice"/>
            </div>
          </div>
          <div v-if="$can(abilities.STATS_PROJECT)" class="row mb-10">
            <div class="col-12">
              <Card class="card shadow-sm p-5">
                <div class="mt-5">
                  <div class="d-flex overflow-auto flex-nowrap gap-4 p-3">
                    <div
                        class="w-225px mb-2 flex-shrink-0"
                        v-for="(stat, index) in projectStatusStat"
                        :key="index"
                    >
                      <div class="card shadow-sm d-flex justify-content-center align-items-start p-5 class-h h-100">
                        <div class="d-flex align-items-center">
                          <span
                              class="badge badge-square fs-6 p-5"
                              :style="{backgroundColor: stat.color}"
                              :class="'badge-'+stat.color"
                          >
                            {{ stat.count }}
                          </span>
                          <div class="fs-6 ms-5">{{ stat[locale] }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <Card :title="$t('projects.list')">
                <template #cardToolbar>
                  <Button
                      type="new"
                      btn-class="btn-sm"
                      btn-text="projects.seeTransferred"
                      @click.prevent="$router.push({ name: 'projects-transferred-index' })" />
                  <Button
                      v-if="$can(abilities.CREATE_PROJECT)"
                      type="new"
                      btn-class="btn-sm mx-3"
                      btn-text="projects.create"
                      @click.prevent="createProject" />
                  
                  <ButtonDropdownFilter
                        :btn-title="$t('projects.label.users')"
                        :options="users"
                        value-field="id"
                        name-field="fullname"
                        v-model="query.user_id"
                    />

                  <ButtonDropdownFilter
                      :btn-title="$t('projects.label.cat')"
                      :options="[...categories]"
                      value-field="id"
                      name-field="name"
                      v-model="query.project_category_id"
                  />
                  <ButtonDropdownFilter
                      :translate="true"
                      :options="projectStates"
                      value-field="value"
                      name-field="name"
                      v-model="query.status"
                  />
                   <Input
                      v-if="(route.query.viewType == modelViewType.grid || !route.query.viewType)" 
                      container-class="me-2 mb-2 mb-md-0"
                      inputClass="form-control form-control-sm"
                      name="input-search"
                      :isInputGroup="true"
                      :placeholder="$t('label.search')"
                      v-model="query.search"
                    >
                      <template #rightAddon>
                        <span class="position-absolute border-0 bg-transparent end-0 top-50 translate-middle">
                          <i class="fa fa-search"></i>
                        </span>
                      </template>
                    </Input>
                  <Button
                      type="button"
                      btn-class="btn-sm btn-danger mx-3"
                      btn-text="button.reset"
                      icon-class="fa fa-filter fs-2"
                      v-if="hasFilters"
                      @click.prevent="resetFilters" />
                  <div class="bg-white shadow justify-content-center align-items-center p-1 me-3 rounded-2 border border-primary">
                    <a  @click="changeModelViewType(modelViewType.list)"
                      :class="(route.query.viewType == modelViewType.list ) ? 'active' : ''"
                      class="btn btn-sm btn-outline-primary text-active-white text-hover-white btn-icon"
                      :title="$t('projects.list')"
                    >
                      <i class="bi bi-list-task fs-6 text-primary"></i>
                    </a>
                    <a @click="changeModelViewType(modelViewType.grid)"
                      :class="(route.query.viewType == modelViewType.grid || !route.query.viewType) ? 'active' : ''"
                      class="btn btn-sm btn-outline-primary text-active-white text-hover-white btn-icon"
                      :title="$t('projects.grid')"
                    >
                      <i class="bi bi-grid-fill fs-6 text-primary"></i>
                    </a>
                  </div>
                </template>
                <CategoryGroupFilter v-model="query.project_category_group_id"/>
                <div v-if="(route.query.viewType == modelViewType.list )" class="card-body">
                  <ListView :filter="query" :office="selectedOffice" @reloadDatatable='loadData'/>
                </div>
                <div v-if="(route.query.viewType == modelViewType.grid || !route.query.viewType)" class="card-body p-0" style="height: 50vh">
                  <GridView :filterData="query" :office="selectedOffice" @reloadDatatable='loadData'/>
                </div>
              </Card>
            </div>
          </div>
        </Loader>
        <NewProjectModal id="create_new_project" :office="selectedOffice" @reloadDatatable='loadData'/>
      </template>
    </SubLayout>
  </template>
  <script setup>
  
  import Card from "@/components/Card.vue";
  import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
  import {useRoute, useRouter} from "vue-router";
  import {useStore} from "vuex";
  import {useLocales} from "@/composables/locales";
  import {computed, ref, watch, onMounted} from "vue";
  import Loader from "@/components/Loader.vue";
  import Button from "@/components/Button.vue";
  import { useModelViewType } from "@/composables/modelViewType";
  import ListView from "./ListView.vue";
  import GridView from "./GridView.vue";
  import NewProjectModal from "../../components/Modals/Project/NewProjectModal.vue";
  import DateRangePicker from "@/components/DateRangePicker.vue";
  import abilities from "@/composables/abilities";
  import OfficeCountCard from "@/components/OfficeCountCard.vue";
  import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";
  import CategoryGroupFilter from "./components/CategoryGroupFilter.vue";
  import Input from "@/components/Input.vue";
  import filterKeys from "@/composables/filterKeys";
  import {useFilters} from "@/composables/useFilters";

  const route = useRoute()
  const router = useRouter()
  const store = useStore()
  const {locale} = useLocales()

  
  const title = ref("breadcrumbs.projects.title")
  const breadcrumbs = ref([
    {
      name: 'breadcrumbs.projects.list',
      route: 'projects'
    }
  ])
  const refreshKey = ref(0)

  const { modelViewType } = useModelViewType()

  const query = ref({
    search: '',
    start_date: '',
    end_date: '',
    contact_id: '',
    project_category_id: '',
    project_category_group_id:'',
    reload: 0,
    status: '',
    user_id: ''
  })

  const selectedOffice = ref('')

  const { filters: oldFilters, hasFilters, updateFilter } = useFilters(filterKeys.PROJECT, query.value)

  const getFirstOffice = ref({})
  const projectStatusStat = ref({})
  const projectStates = ref([
      { value: '', name: 'projects.active' },
      { value: 'ARCHIVED', name: 'projects.archived' },
      { value: 'ALERTED', name: 'projects.alerted' },
      { value: 'SUSPENDED', name: 'projects.suspended' },
      { value: 'INACTIVE', name: 'projects.inactive' },

  ])

  const offices = computed(() => (store.getters['offices/getSocietyOffices']))
  const currentCompany = computed(() => store.getters['offices/getSelectedCompany'])

  const contacts = computed(() => store.getters['contacts/getContacts'])
  const users = computed(() => store.getters["user/getUsers"])
  const categories = computed(() => store.getters['projectCategories/getProjectCategories'])

  watch(query, (value) => {
    if (JSON.stringify(value) !== JSON.stringify(oldFilters.value)) {
      updateFilter(value)
    }

    router.replace({
      name: route.name,
      params: { ...route.params },
      query: { ...route.query, filter: JSON.stringify(query.value) },
    })
  }, { deep: true })

  watch(() => currentCompany.value, (value) => {
    if(value){
     store.dispatch("offices/loadSocietyOffices", value?.id)
    }
  })

  watch(() => offices.value, (value) => {
    if(value){
      getFirstOffice.value = value[0]
    }
  })

  watch(() => getFirstOffice.value, (value) => {
    if(value){
      selectedOffice.value = getFirstOffice.value?.id
    }
  })

  watch(() => [
    query.value.start_date,
    query.value.end_date,
    selectedOffice.value,
    query.value.contact_id,
  ], (newValue) => {
    if(selectedOffice.value){
      loadStat()
    }
  })

  watch(() => selectedOffice.value, (value) => {

    router.replace({
      name: route.name,
      params: { ...route.params },
      query: {...route.query, office: value},
    })
    store.dispatch('projects/getTaskAlertCountByOffice', value)
  }, { deep: true })

  function changeModelViewType (viewType) {
    router.push({ query: { viewType } });
  }
  
  const handleRangeDate2 = (range) => {
    query.value.start_date = range.start_date
    query.value.end_date = range.end_date
  }

  const createProject = () => {
   $("#create_new_project").modal('toggle')
  }

  async function loadStat() {
    if(selectedOffice.value){
      await store.dispatch('projects/getProjectStatusStat', {...query.value, office: selectedOffice.value}).then((res) => {
        projectStatusStat.value = res.data
      })
    }
  }

  function loadData(){
    refreshKey.value++
    query.value.reload += 1
    if(selectedOffice.value){
      loadStat()
    }
  }

  function resetFilters() {
    query.value = {
      search: '',
      start_date: '',
      end_date: '',
      project_category_id: '',
      project_category_group_id:'',
      reload: 0,
      status: '',
      user_id: '',
      contact_id: '',
    }
  }
   
  onMounted(async() => {

    store.dispatch('projects/resetProjects')

    changeModelViewType(modelViewType.grid)

    if(oldFilters.value && JSON.stringify(oldFilters.value) !== JSON.stringify(query.value)) {
      query.value = {
        search: oldFilters.value?.search || '',
        start_date: oldFilters.value?.start_date || '',
        end_date: oldFilters.value?.end_date || '',
        project_category_id: oldFilters.value?.project_category_id || '',
        project_category_group_id: oldFilters.value?.project_category_group_id|| '',
        reload: oldFilters.value?.reload || 0,
        status: oldFilters.value?.status || '',
        user_id: oldFilters.value?.user_id || ''
      }
    }

    if(route.query.office) {
      selectedOffice.value = route.query.office
    }

    if (route.query?.filter) {
      try {
        query.value = { ...JSON.parse(route.query.filter) }
      }catch (e) {
        console.error("Error parsing filter from URL:", e);
      }

    }

    if(currentCompany.value){
      store.dispatch("offices/loadSocietyOffices", currentCompany.value?.id)
    }


    if(selectedOffice.value){
      loadStat()
    }
  })
  
</script>

<style scoped>
  .table-container {
    height: 350px;
    overflow-y: auto;
  }

  thead th {
    position: sticky;
    top: 0;
    background-color: #f8f9fa;
    z-index: 1;
  }

  @media (min-width:992px) {
    .class-h {
      height: 65px !important;
    }
  }

  @media (min-width:1200px) {
    .class-h {
      height: auto !important;
    }
  }
</style>